<template>
  <div class="auth-page">

    <!-- Minimal topbar: icon + wordmark -->
    <div class="auth-topbar">
      <router-link to="/" class="auth-brand" aria-label="Go to homepage">
        <logo variant="icon" theme="auto" style="height: 40px; width: 40px; flex-shrink: 0;" />
        <span class="auth-brand-name">StructEngine</span>
      </router-link>
      <theme-toggle size="small" />
    </div>

    <!-- Centered card -->
    <div class="auth-center">
      <section class="auth-card" aria-labelledby="login-title">

        <!-- Logo mark — anchors brand in the credentials surface -->
        <div class="auth-logo-mark" aria-hidden="true">
          <logo variant="icon" theme="auto" style="height: 40px;" />
        </div>

        <!-- Heading + subtitle -->
        <header class="auth-header">
          <h1 id="login-title" class="auth-title">Welcome back</h1>
          <p class="auth-subtitle">Sign in to continue your structural calculations</p>
        </header>

        <!-- Google OAuth Button -->
        <google-login-button
          @click="handleGoogleLogin"
          :loading="isLoading"
        />

        <!-- Divider -->
        <login-divider />

        <!-- Email/Password Form -->
        <login-form
          v-model:email="formData.email"
          v-model:password="formData.password"
          v-model:remember-me="formData.rememberMe"
          :errors="errors"
          :loading="isLoading"
          @submit="handleEmailLogin"
        />

        <p v-if="authStatusMessage" class="auth-status-message" aria-live="polite">
          {{ authStatusMessage }}
        </p>

        <!-- Session Clear Link (Only on Error) -->
        <div v-if="errors.form" class="session-clear-container">
          <se-button
            type="button"
            variant="text"
            style-set="set8"
            class="session-clear-link"
            @click="clearSessionAndRetry"
          >
            Having trouble? Clear session &amp; retry
          </se-button>
        </div>

      </section>
    </div>

    <!-- Trust strip -->
    <section class="auth-trust-strip" aria-label="Trust indicators">
      <span class="auth-trust-item">
        <v-icon icon="mdi-shield-check" size="14" aria-hidden="true" />
        IS Code Compliant
      </span>
      <span class="auth-trust-item">
        <v-icon icon="mdi-lock" size="14" aria-hidden="true" />
        Encrypted in transit
      </span>
      <span class="auth-trust-item">
        <v-icon icon="mdi-file-document-check-outline" size="14" aria-hidden="true" />
        WCAG 2.2 AA
      </span>
    </section>

  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onBeforeUnmount, onMounted } from 'vue';
import { isAxiosError } from 'axios';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore, type AuthResponse } from '@/stores/auth';
import { apiClient } from '@/services/api';
import { AUTH_ROUTES, AUTH_ERRORS, AUTH_VALIDATION } from '@/constants/auth';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { sanitizeRedirectUrl } from '@/utils/security';
import { runWithAuthRetry } from '@/utils/authNetwork';
import { createRequestGuard } from '@/utils/requestGuard';
import GoogleLoginButton from '@/components/auth/GoogleLoginButton.vue';
import LoginDivider from '@/components/auth/LoginDivider.vue';
import LoginForm from '@/components/auth/LoginForm.vue';
import Logo from '@/components/common/Logo.vue';
import ThemeToggle from '@/components/ui/ThemeToggle.vue';
import SeButton from '@/components/SEButton.vue';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const isLoading = ref(false);
const authStatusMessage = ref('');
const AUTH_REQUEST_TIMEOUT_MS = 45000;
const callbackRequestGuard = createRequestGuard();
const isCallbackProcessing = ref(false);
const emailLoginController = ref<AbortController | null>(null);
const googleLoginController = ref<AbortController | null>(null);
const activeAuthAttemptId = ref<symbol | null>(null);

const logDevError = (...args: unknown[]): void => {
  if (import.meta.env.DEV) {
    console.error(...args);
  }
};

const cancelInFlightAuthRequests = (): void => {
  if (emailLoginController.value) {
    emailLoginController.value.abort();
    emailLoginController.value = null;
  }

  if (googleLoginController.value) {
    googleLoginController.value.abort();
    googleLoginController.value = null;
  }
};

onBeforeUnmount(() => {
  callbackRequestGuard.dispose();
  cancelInFlightAuthRequests();
});

// Get redirect URL from query params or sessionStorage (validated against open redirect)
const getRedirectUrl = (): string => {
  const queryRedirect = (route.query.redirect || route.query.returnUrl) as string | undefined;
  const storedRedirect = sessionStorage.getItem('authReturnUrl');
  sessionStorage.removeItem('authReturnUrl');
  const candidate = queryRedirect || storedRedirect || AUTH_ROUTES.DASHBOARD;
  return sanitizeRedirectUrl(candidate, AUTH_ROUTES.DASHBOARD);
};

async function processOAuthCallback(): Promise<void> {
  if (isCallbackProcessing.value) {
    return;
  }

  isCallbackProcessing.value = true;
  authStatusMessage.value = 'Finalizing sign-in...';
  const callbackRequestId = callbackRequestGuard.begin();

  try {
    await router.replace({ path: '/login', query: {} });

    await runWithAuthRetry(
      async () => {
        await authStore.fetchCsrfToken();
        await authStore.fetchUser();
      },
      2,
      400,
      (nextAttempt) => {
        authStatusMessage.value = `Network is slow. Retrying sign-in (${nextAttempt}/2)...`;
      }
    );

    if (!callbackRequestGuard.isCurrent(callbackRequestId)) {
      return;
    }

    const redirectUrl = getRedirectUrl();
    authStatusMessage.value = '';
    window.location.href = redirectUrl;
  } catch (e) {
    if (!callbackRequestGuard.isCurrent(callbackRequestId)) {
      return;
    }

    logDevError('[Login] OAuth callback processing failed:', e);
    authStatusMessage.value = '';
    errors.form = AUTH_ERRORS.GOOGLE_AUTH_FAILED;
    await authStore.logout();
    await router.replace({ path: '/login', query: {} });
  } finally {
    if (callbackRequestGuard.isCurrent(callbackRequestId)) {
      isCallbackProcessing.value = false;
    }
  }
}

// Handle OAuth callback when page loads
onMounted(async () => {
  const loggedIn = route.query.logged_in as string | undefined;
  const error = route.query.error as string | undefined;

  // Handle OAuth error
  if (error) {
    errors.form = AUTH_ERRORS.GOOGLE_AUTH_FAILED;
    router.replace({ path: '/login', query: {} });
    return;
  }

  // Cookie-based OAuth: server set httpOnly cookie, redirected with ?logged_in=1
  if (loggedIn === '1') {
    await processOAuthCallback();
    return;
  }
  // Legacy token-in-URL fallback removed (PR 2). If a ?token= param arrives,
  // ignore it — the user must complete OAuth via the cookie path.
});

const formData = reactive({
  email: '',
  password: '',
  rememberMe: false,
});

const errors = reactive({
  email: '',
  password: '',
  form: '',
});

const validateForm = (): boolean => {
  errors.email = '';
  errors.password = '';
  errors.form = '';

  let isValid = true;

  if (!formData.email) {
    errors.email = AUTH_ERRORS.FIELD_REQUIRED;
    isValid = false;
  } else if (!AUTH_VALIDATION.EMAIL_REGEX.test(formData.email)) {
    errors.email = AUTH_ERRORS.INVALID_EMAIL;
    isValid = false;
  }

  if (!formData.password) {
    errors.password = AUTH_ERRORS.FIELD_REQUIRED;
    isValid = false;
  } else if (formData.password.length < AUTH_VALIDATION.MIN_PASSWORD_LENGTH) {
    errors.password = AUTH_ERRORS.PASSWORD_TOO_SHORT;
    isValid = false;
  }

  return isValid;
};

const handleEmailLogin = async () => {
  if (!validateForm()) return;

  const attemptId = Symbol('email-login-attempt');
  activeAuthAttemptId.value = attemptId;

  cancelInFlightAuthRequests();
  const controller = new AbortController();
  emailLoginController.value = controller;

  isLoading.value = true;
  authStatusMessage.value = 'Signing in...';
  errors.form = '';

  try {
    const response = await runWithAuthRetry(
      () =>
        apiClient.post<AuthResponse>(
          API_ENDPOINTS.AUTH.LOGIN,
          {
            email: formData.email,
            password: formData.password,
            rememberMe: formData.rememberMe,
          },
          {
            timeout: AUTH_REQUEST_TIMEOUT_MS,
            signal: controller.signal,
          }
        ),
      2,
      400,
      (nextAttempt) => {
        if (activeAuthAttemptId.value === attemptId) {
          authStatusMessage.value = `Network is slow. Retrying sign-in (${nextAttempt}/2)...`;
        }
      }
    );

    if (activeAuthAttemptId.value !== attemptId) {
      return;
    }

    await authStore.login(response.data);
    await authStore.fetchCsrfToken();
    authStatusMessage.value = '';
    await router.push(getRedirectUrl());

  } catch (error: unknown) {
    if (isAxiosError(error) && error.code === 'ERR_CANCELED') {
      return;
    }

    if (activeAuthAttemptId.value !== attemptId) {
      return;
    }

    authStatusMessage.value = '';
    if (isAxiosError(error) && error.response) {
      errors.form = error.response.data?.message ?? AUTH_ERRORS.LOGIN_FAILED;
    } else if (isAxiosError(error) && error.request) {
      errors.form = AUTH_ERRORS.NETWORK_ERROR;
    } else {
      errors.form = AUTH_ERRORS.SERVER_ERROR;
    }
  } finally {
    if (activeAuthAttemptId.value === attemptId) {
      if (emailLoginController.value === controller) {
        emailLoginController.value = null;
      }
      isLoading.value = false;
    }
  }
};

interface GoogleOAuthResponse {
  authorization_url: string;
  state: string;
}

const handleGoogleLogin = async () => {
  const attemptId = Symbol('google-login-attempt');
  activeAuthAttemptId.value = attemptId;

  // Save the redirect URL to sessionStorage before we leave the site for Google OAuth,
  // so that when Google redirects back, processOAuthCallback can find it.
  const queryRedirect = (route.query.redirect || route.query.returnUrl) as string | undefined;
  if (queryRedirect) {
    sessionStorage.setItem('authReturnUrl', queryRedirect);
  }

  cancelInFlightAuthRequests();
  const controller = new AbortController();
  googleLoginController.value = controller;

  isLoading.value = true;
  errors.form = '';

  try {
    authStatusMessage.value = 'Connecting to Google...';
    const response = await runWithAuthRetry(
      () =>
        apiClient.post<GoogleOAuthResponse>(
          API_ENDPOINTS.AUTH.GOOGLE_LOGIN,
          {},
          {
            timeout: AUTH_REQUEST_TIMEOUT_MS,
            signal: controller.signal,
          }
        ),
      2,
      400,
      (nextAttempt) => {
        if (activeAuthAttemptId.value === attemptId) {
          authStatusMessage.value = `Network is slow. Retrying Google sign-in (${nextAttempt}/2)...`;
        }
      }
    );

    if (activeAuthAttemptId.value !== attemptId) {
      return;
    }

    const { authorization_url } = response.data;

    // Redirect to Google's authorization page
    authStatusMessage.value = '';
    window.location.href = authorization_url;
  } catch (error) {
    if (isAxiosError(error) && error.code === 'ERR_CANCELED') {
      return;
    }

    if (activeAuthAttemptId.value !== attemptId) {
      return;
    }

    logDevError('Google OAuth initialization failed:', error);
    authStatusMessage.value = '';
    errors.form = AUTH_ERRORS.GOOGLE_AUTH_FAILED;
  } finally {
    if (activeAuthAttemptId.value === attemptId) {
      if (googleLoginController.value === controller) {
        googleLoginController.value = null;
      }
      isLoading.value = false;
    }
  }
};

/**
 * Clears all client-side auth data and reloads the page
 * Helps users with stale tokens (Safari ITP, expired sessions)
 */
const clearSessionAndRetry = (): void => {
  try {
    // Clear user profile from localStorage (non-sensitive display data)
    localStorage.removeItem('user');

    // Clear all sessionStorage
    sessionStorage.clear();

    // Clear auth-related cookies
    document.cookie.split(';').forEach(c => {
      const cookieName = c.trim().split('=')[0];
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });

    // Reset auth store if available
    authStore.$reset?.();

    // Reload page for clean state
    window.location.href = '/login';
  } catch (error) {
    logDevError('Error clearing session:', error);
    window.location.reload();
  }
};

</script>

<style>
html .auth-page {
  background: var(--bg-canvas) !important;
  background-image: radial-gradient(circle, var(--border-subtle) 1px, transparent 1px) !important;
  background-size: 24px 24px !important;
}

html .auth-card {
  background: var(--bg-surface) !important;
  border: 1px solid var(--border-subtle) !important;
  box-shadow: var(--shadow-card) !important;
  color: var(--text-body) !important;
}

html .auth-card p,
html .auth-card span,
html .auth-card label {
  color: var(--text-body) !important;
}

html .auth-footer-link {
  color: var(--accent) !important;
}

html .auth-card input[type="checkbox"] {
  background-color: var(--bg-surface);
  border-color: var(--border-disabled);
}

html .auth-card input[type="checkbox"]:checked {
  background-color: var(--accent);
  border-color: var(--accent);
}

html .auth-trust-item {
  color: var(--text-muted) !important;
}
</style>

<style scoped>
/* Full-page layout: topbar + centered card + trust strip */
.auth-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  transition: background-color 0.3s ease, background-image 0.3s ease;
}

/* Minimal topbar */
.auth-topbar {
  height: 64px;
  padding: 0 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.auth-brand {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
}

.auth-brand-name {
  font-family: 'Space Grotesk', var(--font-family-heading, sans-serif);
  font-weight: 700;
  font-size: var(--font-size-lg);
  color: var(--brand-color);
}


/* Center area: fills remaining space, vertically centers card */
.auth-center {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 16px 32px;
}

/* Card */
.auth-card {
  width: 100%;
  max-width: 420px;
  border-radius: var(--radius-lg);
  padding: 36px 32px 28px;
  transition: background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
}

/* Logo mark centered at top of card */
.auth-logo-mark {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

/* Heading + subtitle */
.auth-header {
  text-align: center;
  margin-bottom: 28px;
}

.auth-title {
  font-family: 'Space Grotesk', var(--font-family-heading, sans-serif);
  font-weight: 700;
  font-size: var(--font-size-2xl);
  color: var(--text-primary);
  margin-bottom: 8px;
  letter-spacing: -0.01em;
}

.auth-subtitle {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  margin: 0;
}

/* Footer (sign-up link) */
.auth-footer {
  padding-top: 24px;
  text-align: center;
  font-size: var(--font-size-caption);
  color: var(--text-muted);
}

.auth-footer-link {
  font-weight: 600;
  text-decoration: none;
  transition: opacity 0.2s ease;
}

.auth-footer-link:hover {
  opacity: 0.8;
}

.auth-footer-link:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
  border-radius: var(--radius-xs);
}

/* Trust strip */
.auth-trust-strip {
  padding: 0 24px 28px;
  display: flex;
  justify-content: center;
  gap: 24px;
  flex-wrap: wrap;
}

.auth-trust-item {
  font-size: var(--font-size-xs);
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Status message */
.auth-status-message {
  margin-top: 0.75rem;
  text-align: center;
  font-size: var(--font-size-sm);
  color: var(--state-success);
}

.session-clear-container {
  text-align: center;
  margin-top: 1rem;
}

.session-clear-link {
  all: unset;
  cursor: pointer;
  font-size: var(--font-size-sm);
  color: var(--accent);
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: opacity 0.2s ease;
}

.session-clear-link:hover {
  opacity: 0.8;
}

.session-clear-link:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
  border-radius: var(--radius-xs);
}

@media (max-width: 639px) {
  .auth-card {
    padding: 28px 20px 24px;
  }

  .auth-trust-strip {
    gap: 16px;
  }
}
</style>
